import React, { useEffect, useMemo, useState } from 'react'
import { NewTable } from '../../../components/datatable/NewTable'
import { Box, Button, Card, CardContent, Chip, Grid2, Stack, Tooltip } from '@mui/material'
import { CardTitle } from '../../../components/card/NewCard'
import useTitle from '../../../utils/customHook/useTitle'
import i18n from '../../../simple-react-i18n'
import BoundaryError from '../../../log/components/BoundaryError'
import { getFullDate } from '../../../utils/DateUtil'
import SimpleKeyFigurePanel from '../../../components/keyFigures/SimpleKeyFigurePanel'
import DtoKeyFigure from '../../../station/dto/DtoKeyFigure'
import Input from '../../../components/forms/Input'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getSetting } from '../../../utils/SettingUtils'
import { orderBy, upperFirst } from 'lodash'
import useActions from '../../../utils/customHook/useActions'
import CmsAction from '../../../events/actions/CmsAction'
import FixedSlideNav from '../../../components/navbar/FixedSlideNav'
import SuperMultiAutocomplete from '../../../components/forms/SuperMultiAutocomplete'
import moment from 'moment'
import { normalizeString } from '../../../utils/StringUtil'
import LogCmsExport from './LogCMSExport'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import EmailIcon from '@mui/icons-material/Email'
import Checkbox from '../../../components/forms/Checkbox'
import { STATES_CMS } from '../../../events/constants/CMSEventConstants'

const ERROR = 'ERREUR'
const SENT = 'ENVOYE'
const IN_PROGRESS = 'EN COURS'
const NO_INFORMATION = 'AUCUNE INFORMATION'

const MAIL = 'MAIL'
const SMS = 'SMS'

const OPTIONS = [
    { label: IN_PROGRESS, theme: 'warningStatus' },
    { label: SENT, theme: 'validStatus' },
    { label: ERROR, theme: 'errorStatus' },
    { label: NO_INFORMATION, theme: '' },
]

const DEFAULT_VALUE_FILTER = {
    isMail: true,
    isSms: true,
}

const getStatusCms = (s) => {
    switch (s) {
        case STATES_CMS.SENT :
            return SENT
        case STATES_CMS.QUEUED :
            return IN_PROGRESS
        case STATES_CMS.DELIVERED :
            return SENT
        default:
            return s
    }
}

const IconStatusCms = (s) => {
    const status = OPTIONS.find(sm => (sm.label === s))
    return (
        <Chip
            label={status?.label}
            color={status?.theme}
            size={'small'}
        />
    )
}

const Filter = ({
    filterParent,
    changeFilterParent,
}) => {
    const [filter, setFilter] = useState(filterParent)
    return (
        <Card>
            <CardContent>
                <Grid2 container spacing={1} justifyContent={'space-between'} alignItems='center'>
                    <Grid2 size={3}>
                        <Input
                            title={i18n.search}
                            value={filter.searchValue}
                            onChange={ v => setFilter(prev => ({ ...prev, searchValue: v })) }
                            onEnterKeyPress={ v => changeFilterParent({ ...filter, searchValue: v }) }
                            data-cy={'search_input'}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <SuperMultiAutocomplete
                            options={ OPTIONS }
                            label={i18n.status}
                            values={ filter?.status }
                            noNullValue
                            multiple
                            keyValue={'label'}
                            onChange={v => setFilter(prev => ({ ...prev, status: v })) }
                        />
                    </Grid2>
                    <Grid2 size={1.5} paddingLeft={2}>
                        <Checkbox
                            checked={filter.isMail}
                            label={<EmailIcon />}
                            onChange={() => setFilter(prev => ({ ...prev, isMail: !prev.isMail })) }
                        />
                    </Grid2>
                    <Grid2 size={1.5}>
                        <Checkbox
                            checked={filter.isSms}
                            label={<SmartphoneIcon />}
                            onChange={() => setFilter(prev => ({ ...prev, isSms: !prev.isSms })) }
                        />
                    </Grid2>
                    <Grid2 size={3} spacing={1} container justifyContent='flex-end'>
                        <Button
                            variant='outlined'
                            onClick={() => {
                                setFilter(DEFAULT_VALUE_FILTER)
                                changeFilterParent(DEFAULT_VALUE_FILTER)
                            }}
                            data-cy={'reset_button'}
                        >
                            {i18n.reinit}
                        </Button>
                        <Button
                            variant='contained'
                            onClick={() => changeFilterParent(filter)}
                            data-cy={'search_button'}
                        >
                            {i18n.search}
                        </Button>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    )
}
Filter.propTypes = {
    filterParent: PropTypes.shape({
        searchValue: PropTypes.string,
    }),
    changeFilterParent: PropTypes.func,
}

const SmsMailDashboardApp = ({}) => {
    const dispatch = useDispatch()

    const [cms, setCms] = useState([])
    const [filter, setFilter] = useState(DEFAULT_VALUE_FILTER)
    const [isOpen, setIsOpen] = useState(false)

    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)


    const HEADERS = [
        { key: 'typeIcon', value: i18n.jobType },
        'status',
        { key: 'dateSend', value: i18n.date },
        { key: 'recipient', value: i18n.destinataire },
        /*
            { key: 'jobexecutionid', value: 'ID d\'exécution de la tâche' },
        */
        { key: 'contactCode', value: i18n.fullName },
        { key: 'supplierContactCode', value: i18n.supplyTime }, //  code provider
        { key: 'supplierGroupCode', value: i18n.supplierCodeGroup },/*
        'updateDate',
        'updateLogin',*/
    ]

    useTitle(() => [{
        title: i18n.smsMail,
        href: '/cms',
    }, {
        title: i18n.dashboard,
        href: 'cms/dashboard',
    }], [])

    useActions(() => {
        return {
            exportList: [{
                onClick: () => setIsOpen(true),
                label: i18n.excel,
            }],
        }
    }, [])

    useEffect(() => {
        dispatch(CmsAction.fetchCmsSend()).then(r => {
            setCms(r)
        })
    }, [])

    const {
        applicationSettings,
    } = useSelector(store => ({
        applicationSettings: store.AdministrationReducer.applicationSettings,
    }), shallowEqual)

    const cmsFormated = useMemo(() => {
        const cmsFilterStatus = (filter.status?.length > 0) ? cms.filter(c => filter.status.includes(c.status)) : cms
        const formatCMS = cmsFilterStatus.map(cms => {
            const status = getStatusCms(cms.status)
            return {
                type: cms.recipient.includes('@') ? MAIL : SMS,
                typeIcon: cms.recipient.includes('@') ? <Tooltip title={i18n.email}><EmailIcon/></Tooltip> :
                    <Tooltip title={'SMS'}><SmartphoneIcon/></Tooltip>,
                ...cms,
                contactCode: contacts.find(c => c.id === cms.contactCode)?.name ?? i18n.unknownContact,
                dateSend: getFullDate(cms.dateSend),
                date: cms.dateSend,
                updateDate: getFullDate(cms.updateDate),
                valueStatus: status,
                status: { value: IconStatusCms(status), status, sortValue: status },
            }
        })
        const searchValueFormat = normalizeString(filter.searchValue)
        const filterType = formatCMS.filter(f =>
            (filter.isMail && filter.isSms) ||
            (filter.isMail && f.type === MAIL) ||
            (filter.isSms && f.type === SMS),
        )
        return filter.searchValue ? filterType.filter(p => normalizeString([p.dateSend, p.recipient].join('   ')).includes(searchValueFormat)) : filterType
    }, [cms, filter])

    const cmsSend = cms.filter(cms => cms.status === SENT).length
    const cmsNotSend = cms.filter(cms => cms.status === ERROR).length
    const cmsNoDetail = cms.filter(cms => cms.status === NO_INFORMATION).length
    const cmsInProgress = cms.filter(cms => cms.status === IN_PROGRESS).length


    const todayCms = cms.filter(item =>
        item.dateSend >= moment().startOf('day').valueOf() &&
        item.dateSend <= moment().endOf('day').valueOf(),
    ).length

    const weeklyCms = cms.filter(item =>
        item.dateSend >= moment().startOf('isoWeek').valueOf() &&
        item.dateSend <= moment().endOf('isoWeek').valueOf(),
    ).length

    const monthlyCms = cms.filter(item =>
        item.dateSend >= moment().startOf('month').valueOf() &&
        item.dateSend <= moment().endOf('month').valueOf(),
    ).length

    const keyFigureData = [
        new DtoKeyFigure({
            title: i18n.today,
            value: todayCms,
        }),
        new DtoKeyFigure({
            title: i18n.thisWeek,
            value: weeklyCms,
        }),
        new DtoKeyFigure({
            title: i18n.thisMonth,
            value: monthlyCms,
        }),
    ]

    return (
        <Box>
            <FixedSlideNav links={[
                {
                    href: '/cms',
                    icons: 'dashboard',
                    name: i18n.dashboard,
                },
            ]}
            />
            <Box className='content-page no-padding fullscreen'>
                <BoundaryError>
                    <Grid2 container padding={1} spacing={1}>
                        <Grid2 size={10}>
                            <Stack spacing={1}>
                                <Card>
                                    <CardTitle title={ i18n.generalInfos } />
                                    <CardContent>
                                        <Grid2 container>
                                            <Grid2 size={6}>
                                                <Grid2 container alignItems='center'>
                                                    <Grid2 size={5} textAlign='right' paddingRight={1}>
                                                        <h6 className='bold'>{i18n.setting}</h6>
                                                    </Grid2>
                                                    <Grid2 size={7}>
                                                        <h6>{upperFirst(getSetting(applicationSettings, 'smsSettings'))}</h6>
                                                    </Grid2>
                                                </Grid2>
                                            </Grid2>
                                        </Grid2>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent>
                                        <Grid2 container spacing={1}>
                                            <Grid2 size={3}>
                                                <Chip
                                                    label={ERROR}
                                                    color={'errorStatus'}
                                                    size={'small'}
                                                    sx={{ marginRight: 1 }}
                                                    onClick={() => setFilter({ ...filter, status: [ERROR] })}
                                                />
                                                {cmsNotSend} {i18n.messages}
                                            </Grid2>
                                            <Grid2 size={3}>
                                                <Chip
                                                    label={IN_PROGRESS}
                                                    color={'warningStatus'}
                                                    size={'small'}
                                                    sx={{ marginRight: 1 }}
                                                    onClick={() => setFilter({ ...filter, status: [IN_PROGRESS] })}
                                                />
                                                {cmsInProgress} {i18n.messages}
                                            </Grid2>
                                            <Grid2 size={3}>
                                                <Chip
                                                    label={SENT}
                                                    color={'validStatus'}
                                                    size={'small'}
                                                    sx={{ marginRight: 1 }}
                                                    onClick={() => setFilter({ ...filter, status: [SENT] })}
                                                />
                                                {cmsSend} {i18n.messages}
                                            </Grid2>
                                            <Grid2 size={3}>
                                                <Chip
                                                    label={NO_INFORMATION}
                                                    color={'primay'}
                                                    size={'small'}
                                                    sx={{ marginRight: 1 }}
                                                    onClick={() => setFilter({ ...filter, status: [NO_INFORMATION] })}
                                                />
                                                {cmsNoDetail} {i18n.messages}
                                            </Grid2>
                                        </Grid2>
                                    </CardContent>
                                </Card>
                                <Filter
                                    filterParent={ filter }
                                    changeFilterParent={ setFilter }
                                />
                                <NewTable
                                    rows={orderBy(cmsFormated, 'date', 'desc')}
                                    headers={HEADERS}
                                    /* onClickRow={s => {
                                                dispatch(push(`/cms/${s.idCms}`))
                                            }}*/
                                />
                            </Stack>
                        </Grid2>
                        <Grid2 size={2}>
                            <SimpleKeyFigurePanel
                                data={keyFigureData}
                                oneStatPerLine
                            />
                        </Grid2>
                    </Grid2>
                    <LogCmsExport
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        cms={orderBy(cmsFormated, 'date', 'desc')}
                        headers={[
                            'status',
                            'dateSend',
                            'recipient',
                            'jobexecutionid',
                            'contactCode',
                            'supplierContactCode',
                            'supplierGroupCode',
                            'updateDate',
                            'updateLogin',
                        ]}
                    />
                </BoundaryError>
            </Box>
        </Box>
    )
}

export default SmsMailDashboardApp